/**
 * The six controls that narrow the portfolio.
 *
 * @module components/portfolio/PortfolioFilters
 */

import React from "react";

import type { PortfolioFiltersModel } from "@/components/portfolio/usePortfolioFilters";
import { FilterBar, FormField, SelectField, TextField } from "@/components/ui";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "current", label: "Current" },
  { value: "approved-future", label: "Approved future" },
  { value: "superseded", label: "Superseded" },
];

export interface PortfolioFiltersProps {
  model: PortfolioFiltersModel;
  /** How many revisions are showing, so the count sits with its controls. */
  shown: number;
  total: number;
  /** `flush` sits inside a surface that already has a border, like a master list. */
  variant?: "card" | "flush";
}

/**
 * @example
 * ```tsx
 * <PortfolioFilters model={filters} shown={filters.filtered.length} total={all.length} />
 * ```
 */
export function PortfolioFilters({
  model,
  shown,
  total,
  variant = "card",
}: PortfolioFiltersProps): React.ReactElement {
  return (
    <FilterBar
      aria-label="Filter approved revisions"
      variant={variant}
      active={model.active}
      onClear={model.clear}
      summary="Filter approved revisions"
      // On the summary, not among the controls: the collapsed panel would hide
      // the one number on the screen. "programmes", because the table renders
      // one row per programme; the client admin approval spec asserts the copy.
      count={`${shown} of ${total} approved programmes shown`}
      countTestId="portfolio-result-count"
      data-testid="portfolio-filters"
    >
      <div className="grid gap-3">
        <FormField label="Status" htmlFor="portfolio-status-filter">
          <SelectField
            id="portfolio-status-filter"
            value={model.status}
            onChange={(value) => model.setStatus(value as PortfolioFiltersModel["status"])}
            options={STATUS_OPTIONS}
            data-testid="portfolio-status-filter"
          />
        </FormField>
        <FormField label="Level" htmlFor="portfolio-level-filter">
          <SelectField
            id="portfolio-level-filter"
            value={model.level}
            onChange={model.setLevel}
            options={[
              { value: "all", label: "All levels" },
              ...model.levels.map((level) => ({ value: level, label: `NFQ ${level}` })),
            ]}
            data-testid="portfolio-level-filter"
          />
        </FormField>
        <FormField label="Award type" htmlFor="portfolio-award-type-filter">
          <SelectField
            id="portfolio-award-type-filter"
            value={model.awardType}
            onChange={model.setAwardType}
            options={[
              { value: "all", label: "All award types" },
              ...model.awardTypes.map((award) => ({ value: award, label: award })),
            ]}
            data-testid="portfolio-award-type-filter"
          />
        </FormField>
        <FormField
          label="In force on"
          htmlFor="portfolio-effective-on-filter"
          helpText="Shows the revision of each programme that was current on this date."
        >
          <TextField
            id="portfolio-effective-on-filter"
            type="date"
            value={model.effectiveOn}
            onChange={model.setEffectiveOn}
            data-testid="portfolio-effective-on-filter"
          />
        </FormField>
        <FormField label="Search" htmlFor="portfolio-search">
          <TextField
            id="portfolio-search"
            value={model.text}
            onChange={model.setText}
            placeholder="Title, code, award, revision"
            data-testid="portfolio-search"
          />
        </FormField>
      </div>
    </FilterBar>
  );
}
